import { Page } from "../../templates/Page";
import { FC, useEffect, useState } from "react";
import styled from "styled-components";
import { mobile, tablet } from "components/templates/Breakpoints";
import { ResourceBar } from "components/resources/resourceHub/ResourceBar";
import { LatestCaseStudy } from "utils/server";
import { Category } from "pages/resource-hub";
import { usePathname } from "next/navigation";
import {
  ContentType,
  PostAttributes,
  getReadMinutesFromHtml,
  getStrapiImageByBreakpoint,
  ink,
} from "@utils";
import { StrapiLocale, YuLifeLocale, useBreakpoints } from "@hooks";
import { BasicResourceCard } from "components/resources/resourceHub/BasicResourceCard";
import { Spacer } from "components/templates/Spacer";
import { strapiClientRequestsService } from "services/strapiClientRequestsService";
import { Button } from "components/atoms/Button";
import { NON_ENGLISH_LOCALES, NUMBER_OF_POSTS_TO_FETCH } from "utils/_constants";
import ResourcesBreadcrumb from "components/resources/resourceArticle/ResourcesBreadcrumb";
import { getStrapiLocale } from "utils/locales/strapi";
import { H2 } from "components/atoms/Heading";
import { P1 } from "components/atoms/Paragraph";
import { LocationInfo } from "components/resources/resourceArticle/types";
import { getNoPostsTitle } from "./utils";

interface ContentHomePageProps {
  latestCaseStudy: LatestCaseStudy;
  categories: Category[];
  contentType: ContentType;
  slug?: string;
  initialPosts: PostAttributes[];
  locale: YuLifeLocale;
}

const ContentHomePage: FC<ContentHomePageProps> = ({
  latestCaseStudy,
  categories,
  slug,
  contentType,
  initialPosts,
  locale,
}) => {
  const { isDesktop, isMobile } = useBreakpoints();
  const [numberOfVisiblePosts, setNumberOfVisiblePosts] = useState(initialPosts?.length);
  const [preLoadedPosts, setPreLoadedPosts] = useState<PostAttributes[]>(initialPosts);
  const [visiblePosts, setVisiblePosts] = useState<PostAttributes[]>(initialPosts);
  const [noMorePosts, setNoMorePosts] = useState(false);

  const path = usePathname();
  const basePath = path?.split("/")?.[1] || "";
  const firstPost = visiblePosts?.[0];
  const isOnBlogPage = contentType === ContentType.blog;
  const hasMorePosts = !noMorePosts;
  const isNonBlogMobile = !isOnBlogPage && isMobile;

  const categoryName = categories.find((category) => category.slug === slug)?.name;

  const locationInfo: LocationInfo = {
    contentType,
    path: basePath,
    categoryName: categoryName,
    slug: slug || "",
  };

  const contentTypeServiceMap = {
    blog: strapiClientRequestsService.getBlogs,
    caseStudies: strapiClientRequestsService.getCaseStudies,
    newsAndEvents: strapiClientRequestsService.getNewsEvents,
  };

  const getPosts = async (startIndex: number) => {
    const serviceMethod = contentTypeServiceMap[contentType];

    const strapiLocale = getStrapiLocale(locale);
    const isNonEnglishLocale = NON_ENGLISH_LOCALES.includes(locale);

    let localeArg = "" as StrapiLocale;
    let filters = {};

    switch (contentType) {
      case ContentType.newsEvents:
        localeArg = isNonEnglishLocale ? strapiLocale : "all";
        filters = isNonEnglishLocale ? undefined : { locale: { in: ["en", strapiLocale] } };
        break;
      case ContentType.caseStudy:
        localeArg = isNonEnglishLocale ? strapiLocale : "en";
        break;
      case ContentType.blog:
        localeArg = strapiLocale;
        break;
      default:
        localeArg = strapiLocale;
    }

    if (slug) {
      const tagFilter =
        contentType === ContentType.blog
          ? { tags: { slug: { eq: slug } } }
          : { category: { slug: { eq: slug } } };
      filters = { ...filters, ...tagFilter };
    }

    const result = await serviceMethod({
      locale: localeArg,
      pagination: { start: startIndex, limit: NUMBER_OF_POSTS_TO_FETCH },
      filters: filters,
    });

    return result;
  };

  useEffect(() => {
    setVisiblePosts(initialPosts);
  }, [initialPosts]);

  useEffect(() => {
    const loadMorePosts = async () => {
      setNoMorePosts(false);

      const result = await getPosts(numberOfVisiblePosts);

      if (result.length === 0) {
        setNoMorePosts(true);
        return;
      }

      setPreLoadedPosts(result);
    };
    loadMorePosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPosts]);

  const loadMorePosts = async () => {
    setVisiblePosts([...visiblePosts, ...preLoadedPosts]);
    setNumberOfVisiblePosts(numberOfVisiblePosts + preLoadedPosts.length);

    const result = await getPosts(numberOfVisiblePosts + preLoadedPosts.length);

    if (result.length === 0) {
      setNoMorePosts(true);
    }

    setPreLoadedPosts(result);
  };

  const title = getNoPostsTitle(locale);

  return (
    <Page navColor="transparent" hero={null} fullScreenHero latestCaseStudy={latestCaseStudy}>
      <ResourceBar {...locationInfo} categories={categories} locale={locale} />
      <Margin>
        <Container>
          <ResourcesBreadcrumb
            locationInfo={locationInfo}
            pageType={locationInfo.slug ? "category" : "content"}
            locale={locale}
          />
          <Spacer height={["5rem", "3rem", "1.5rem"]} />
          {visiblePosts.length === 0 && (
            <>
              <H2>{title.title}</H2>
              <P1 color={ink.inkSubtle}>{title.subtitle}</P1>
              <Spacer height={["4rem", "12rem"]} />
            </>
          )}
          {visiblePosts.length > 0 && (
            <>
              <BasicResourceCard
                title={firstPost.title}
                tag={isNonBlogMobile ? null : firstPost.tags?.data[0]?.attributes?.name}
                category={isOnBlogPage ? null : firstPost.category?.data?.attributes?.name}
                thumbnailSrc={getStrapiImageByBreakpoint(
                  firstPost.featuredImage?.data?.attributes?.formats,
                  isMobile ? "medium" : "xxlarge",
                )}
                thumbnailAlt={firstPost.featuredImage?.data?.attributes?.alternativeText}
                duration={getReadMinutesFromHtml(firstPost.post, locale)}
                excerpt={firstPost.excerpt}
                slug={firstPost.slug}
                contentType={contentType}
                pillPosition="top"
                imgMargin="1rem"
                imgHeight={isDesktop ? "100%" : "16.25rem"}
                nextImageHeight={600}
                nextImageWidth={1420}
                imgWidth="100%"
                priority
              />
              <Flex>
                {visiblePosts.length > 1 &&
                  visiblePosts.slice(1).map((card, index) => {
                    return (
                      <BasicResourceCard
                        title={card.title}
                        key={card.title + index}
                        tag={isNonBlogMobile ? null : card.tags?.data[0]?.attributes?.name}
                        category={isOnBlogPage ? null : card.category?.data?.attributes?.name}
                        thumbnailSrc={getStrapiImageByBreakpoint(
                          card.featuredImage?.data?.attributes?.formats,
                          "medium",
                        )}
                        thumbnailAlt={card.featuredImage?.data?.attributes?.alternativeText}
                        duration={isDesktop && getReadMinutesFromHtml(card.post, locale)}
                        excerpt={!isMobile ? card.excerpt : null}
                        contentType={contentType}
                        slug={card.slug}
                        imgWidth={isDesktop ? "21.75rem" : "50%"}
                        isHorizontal
                        pillPosition={isDesktop ? "top" : "bottom"}
                        truncation={isMobile ? 5 : 4}
                        imgHeight={isMobile && "9.25rem"}
                      />
                    );
                  })}
                {hasMorePosts && (
                  <ButtonContainer>
                    <Button onClick={loadMorePosts}>Load more articles</Button>
                  </ButtonContainer>
                )}
              </Flex>
            </>
          )}
        </Container>
      </Margin>
    </Page>
  );
};

export default ContentHomePage;

const Flex = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  margin-top: 5rem;

  ${tablet} {
    gap: 2rem;
  }
`;

const ButtonContainer = styled.div`
  margin: 0 auto;
`;

const Margin = styled.div<{ $backgroundColor?: string }>`
  width: 100%;
  padding: 2rem 10.5%;
  padding-bottom: 8rem;

  ${tablet} {
    padding: 3rem 6%;
  }

  ${mobile} {
    padding: 2rem 1.5rem;
  }
`;

const Container = styled.div`
  max-width: 70.5rem;
  margin: 0 auto;
`;
